import { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ChatMessage from "./ChatMessage";

interface QAPair {
  id: string;
  question: string;
  answer: string;
  followUps?: string[];
}

// RASCUNHO — conteúdo educativo/genérico para aprovação antes de publicar.
// Sem dosagem (mg) nem diagnóstico nomeado, por decisão do time (ver SAL-252).
// Estrutura em 2 níveis: perguntas de partida (ver starterIds) + follow-ups
// que aparecem logo depois da resposta do pai, simulando uma consulta real.
const qaPairs: QAPair[] = [
  {
    id: "vias-administracao",
    question: "Quais são as formas de uso da cannabis medicinal?",
    answer:
      "Existem diferentes vias, como óleos de uso sublingual, cápsulas e vaporização. Cada uma tem características próprias de absorção e início de efeito, e a escolha depende da avaliação clínica de cada paciente.",
    followUps: ["via-mais-rapida", "uso-pediatrico"],
  },
  {
    id: "cbd-thc",
    question: "Qual a diferença entre CBD e THC?",
    answer:
      "CBD (canabidiol) e THC (tetrahidrocanabinol) são os dois principais canabinoides estudados. O THC tem efeito psicoativo e o CBD não; ambos interagem com o sistema endocanabinoide de formas distintas, e a proporção entre eles costuma orientar a escolha da formulação.",
    followUps: ["cbd-psicoativo", "proporcao-padrao"],
  },
  {
    id: "interacoes",
    question: "A cannabis medicinal pode interagir com outros remédios?",
    answer:
      "Sim, interações são possíveis — por exemplo, com medicamentos metabolizados pelas mesmas vias hepáticas. É por isso que a avaliação de um profissional prescritor, considerando o histórico completo do paciente, é essencial antes de qualquer tratamento.",
    followUps: ["classes-atencao", "dr-weedy-interacoes"],
  },
  {
    id: "evidencias",
    question: "Existem estudos científicos sobre cannabis medicinal?",
    answer:
      "Sim, há um corpo crescente de literatura científica — incluindo ensaios clínicos e revisões sistemáticas — investigando seu uso em diversos quadros clínicos. As evidências variam em qualidade e indicação, e devem ser interpretadas por um profissional.",
    followUps: ["onde-publicadas", "evidencias-variam"],
  },
  {
    id: "acompanhamento",
    question: "O Dr. Weedy ajuda no acompanhamento do paciente?",
    answer:
      "O assistente apoia o profissional trazendo informações organizadas para orientar consultas de retorno e monitoramento — a decisão clínica e a conduta continuam sendo sempre do médico ou dentista responsável.",
    followUps: ["como-funciona-retorno", "substitui-prontuario"],
  },
  {
    id: "regulamentacao",
    question: "Como funciona a regulamentação da cannabis medicinal no Brasil?",
    answer:
      "A prescrição é feita por médicos ou dentistas devidamente habilitados, seguindo as normas vigentes para produtos à base de cannabis. O processo envolve etapas regulatórias específicas, que variam conforme o tipo de produto e a via de acesso do paciente.",
    followUps: ["quem-pode-prescrever", "autorizacao-especial"],
  },
  {
    id: "efeitos-colaterais",
    question: "Quais os efeitos colaterais mais comuns?",
    answer:
      "Os efeitos mais relatados costumam ser leves e transitórios, como sonolência, boca seca ou tontura, tendendo a diminuir com ajuste de tratamento. O perfil de segurança é individual e deve ser acompanhado pelo médico responsável.",
    followUps: ["efeitos-graves", "conduta-efeito-adverso"],
  },
  {
    id: "perfil-paciente",
    question: "Que tipo de paciente costuma se beneficiar desse tratamento?",
    answer:
      "O perfil varia bastante conforme a condição e a resposta individual — por isso a anamnese completa e o histórico clínico são etapas essenciais antes de qualquer indicação, sempre conduzidas pelo médico responsável.",
    followUps: ["contraindicacao-geral", "paciente-naive"],
  },
  // Follow-ups (sem followUps próprios — mantém a árvore em 2 níveis)
  {
    id: "via-mais-rapida",
    question: "Qual via tem efeito mais rápido?",
    answer:
      "Vias inalatórias tendem a ter início de efeito mais rápido que vias orais ou sublinguais, que passam por absorção gastrointestinal ou pela mucosa. A escolha considera a urgência clínica, o perfil do paciente e a formulação disponível.",
  },
  {
    id: "uso-pediatrico",
    question: "O óleo pode ser usado em crianças?",
    answer:
      "O uso em pacientes pediátricos segue avaliação clínica específica e critérios próprios de indicação. Cabe ao médico responsável avaliar caso a caso, considerando idade, condição e evidências disponíveis para a faixa etária.",
  },
  {
    id: "cbd-psicoativo",
    question: "O CBD isolado tem efeito psicoativo?",
    answer:
      "Não. O CBD isoladamente não produz o efeito psicoativo associado ao THC. Por isso, formulações com predominância de CBD costumam ser a primeira escolha em diversos contextos clínicos, com ajuste conforme resposta.",
  },
  {
    id: "proporcao-padrao",
    question: "Existe uma proporção CBD:THC padrão?",
    answer:
      "Não existe uma proporção universal — a relação entre CBD e THC é individualizada, considerando a condição do paciente, resposta terapêutica e tolerância. Essa é uma das decisões centrais do plano de tratamento, definida pelo médico.",
  },
  {
    id: "classes-atencao",
    question: "Quais classes de medicamentos exigem mais atenção?",
    answer:
      "Medicamentos metabolizados por enzimas hepáticas específicas merecem atenção redobrada, já que os canabinoides também são processados por essas vias. Anticoagulantes, alguns anticonvulsivantes e sedativos costumam entrar nessa análise.",
  },
  {
    id: "dr-weedy-interacoes",
    question: "Como o Dr. Weedy ajuda a identificar isso?",
    answer:
      "O agente de segurança farmacológica analisa a lista de medicamentos em uso do paciente e sinaliza possíveis interações relevantes, servindo como apoio adicional à checagem clínica — a decisão final é sempre do médico.",
  },
  {
    id: "onde-publicadas",
    question: "Onde essas evidências são publicadas?",
    answer:
      "Em periódicos científicos indexados em bases como PubMed e Cochrane, incluindo ensaios clínicos randomizados, revisões sistemáticas e meta-análises. O Dr. Weedy referencia esse tipo de fonte ao trazer informações.",
  },
  {
    id: "evidencias-variam",
    question: "As evidências são as mesmas para todas as condições?",
    answer:
      "Não. O nível de evidência varia bastante conforme a condição estudada — algumas áreas têm corpo de pesquisa mais consolidado que outras. É importante considerar essa variação ao interpretar as informações disponíveis.",
  },
  {
    id: "como-funciona-retorno",
    question: "Como funciona o retorno do paciente?",
    answer:
      "O Dr. Weedy pode ajudar a organizar informações para consultas de retorno, trazendo um histórico estruturado da conversa clínica anterior — mas a condução do retorno e as decisões continuam sendo do médico responsável.",
  },
  {
    id: "substitui-prontuario",
    question: "Isso substitui o prontuário do paciente?",
    answer:
      "Não. O Dr. Weedy é uma ferramenta de apoio à decisão clínica, não um sistema de prontuário. As informações relevantes devem continuar sendo registradas no prontuário oficial do paciente, conforme as práticas da clínica.",
  },
  {
    id: "quem-pode-prescrever",
    question: "Qualquer médico pode prescrever?",
    answer:
      "A prescrição é feita por médicos ou dentistas devidamente habilitados, sem necessidade de ser especialista em uma área específica — mas é importante estar atualizado sobre as diretrizes regulatórias vigentes.",
  },
  {
    id: "autorizacao-especial",
    question: "O paciente precisa de autorização especial?",
    answer:
      "Dependendo do tipo de produto e via de acesso, pode haver etapas administrativas específicas, como importação ou cadastro junto a órgãos regulatórios. Esses detalhes fazem parte da orientação que o médico fornece ao paciente.",
  },
  {
    id: "efeitos-graves",
    question: "Esses efeitos costumam ser graves?",
    answer:
      "Na maioria dos casos, os efeitos relatados são leves e transitórios, tendendo a diminuir com ajuste de tratamento. Efeitos mais significativos são menos comuns, mas reforçam a importância do acompanhamento médico contínuo.",
  },
  {
    id: "conduta-efeito-adverso",
    question: "O que fazer se o paciente relatar um efeito adverso?",
    answer:
      "O médico avalia a intensidade e a relação com o tratamento, podendo ajustar a formulação, a via de administração ou reavaliar a indicação. Efeitos relevantes também podem ser documentados conforme as práticas de farmacovigilância da clínica.",
  },
  {
    id: "contraindicacao-geral",
    question: "Existe alguma contraindicação geral?",
    answer:
      "Sim, algumas situações clínicas pedem avaliação mais cautelosa antes da indicação — por isso a anamnese completa e o histórico do paciente são etapas essenciais, sempre conduzidas pelo médico responsável.",
  },
  {
    id: "paciente-naive",
    question: "Pacientes sem experiência prévia podem começar o tratamento?",
    answer:
      "Sim. Pacientes sem experiência prévia com cannabis medicinal podem ser candidatos, geralmente com uma abordagem mais gradual na fase inicial do tratamento, conforme orientação médica.",
  },
  // Casos clínicos: cadeia de 3 falas (mesmo mecanismo de followUps, só mais
  // profundo) simulando o médico construindo contexto até uma resposta final
  // que sintetiza múltiplos agentes. Valores clínicos exatos (dose, prazo,
  // grau de evidência) aparecem "censurados" via `code inline` do markdown
  // — mostra que a resposta real tem esse nível de especificidade sem
  // publicar um número não revisado. Ver decisão em SAL-252.
  {
    id: "caso1-inicio",
    question:
      "Tenho um paciente com dor crônica que não respondeu bem a tratamentos convencionais — por onde começar a avaliação para cannabis medicinal?",
    answer:
      "Para casos de dor crônica refratária, o primeiro passo é reunir uma anamnese completa: histórico de tratamentos já tentados, comorbidades, medicações em uso e expectativas do paciente. Isso permite ao **Agente de Diagnóstico** levantar hipóteses de indicação e ao **Agente de Segurança** já triar possíveis contraindicações antes de avançar.",
    followUps: ["caso1-medicacao"],
  },
  {
    id: "caso1-medicacao",
    question: "Ele já usa um anticonvulsivante para outra condição. Isso muda alguma coisa?",
    answer:
      "Sim — essa é exatamente a informação que aciona o **Agente de Interações**: alguns anticonvulsivantes são metabolizados pelas mesmas vias hepáticas que os canabinoides, então a combinação pede acompanhamento mais próximo. Com esse dado, o **Agente de Literatura** também passa a priorizar estudos que envolvam esse tipo de comedicação, trazendo contexto mais relevante para o seu caso.",
    followUps: ["caso1-sintese"],
  },
  {
    id: "caso1-sintese",
    question: "Juntando tudo isso, o que vocês recomendam considerar antes da consulta de indicação?",
    answer:
      "Com o que você trouxe até aqui — dor crônica refratária e uso de anticonvulsivante — o painel organiza a resposta em camadas: **Literatura** aponta grau de evidência `[██]` para esse quadro quando outras abordagens já foram esgotadas; **Farmacologia** sugere início com CBD `[██] mg/dia`, com titulação gradual ao longo de `[██] semanas`; **Segurança** sinaliza a interação a monitorar com o anticonvulsivante — sem contraindicação absoluta identificada — e recomenda reavaliação em `[██] dias`. *Os valores exatos aparecem na plataforma completa; aqui na demonstração eles ficam propositalmente ocultos.* A decisão final sobre indicação, formulação e ajuste continua sempre sua.",
  },
  {
    id: "caso2-inicio",
    question:
      "Tenho um paciente idoso, polimedicado, considerando cannabis medicinal para melhorar a qualidade do sono. Como conduzir?",
    answer:
      "Nesse perfil, o ponto de partida é mapear toda a lista de medicações em uso — é o gatilho que o **Agente de Interações** precisa para cruzar contra as vias metabólicas dos canabinoides. Em paralelo, o **Agente de Diagnóstico** já sinaliza que, em pacientes idosos, a resposta tende a ser mais sensível, então a abordagem costuma ser mais conservadora desde o início.",
    followUps: ["caso2-sedativo"],
  },
  {
    id: "caso2-sedativo",
    question: "Ele também toma um sedativo para dormir.",
    answer:
      "Combinar canabinoides com outro sedativo é o tipo de sobreposição que o **Agente de Segurança** prioriza sinalizar — o risco maior não é de interação medicamentosa direta, mas de efeito aditivo, como sonolência ou tontura, especialmente relevante num paciente idoso. Nesse cenário, o **Agente de Protocolos** costuma trazer guidelines que recomendam reavaliações mais frequentes nas primeiras semanas.",
    followUps: ["caso2-sintese"],
  },
  {
    id: "caso2-sintese",
    question: "No fim, que tipo de raciocínio geral costuma sair dessas informações?",
    answer:
      "Com o perfil que você descreveu — idade avançada, polifarmácia e uso de sedativo — o painel organiza assim: **Farmacologia** sugere início com CBD `[██] mg/dia`, dose bem mais conservadora que o padrão adulto; **Segurança** destaca o efeito aditivo com o sedativo como o principal ponto de atenção — não uma contraindicação — com janela de observação de `[██] horas` após o início; **Acompanhamento** sugere reavaliação em `[██] dias`, mais próxima que o padrão habitual. *Valores exatos disponíveis na plataforma completa — aqui ficam ocultos de propósito.* Você segue com a palavra final sobre indicação e ajustes.",
  },
];

const starterIds = [
  "vias-administracao",
  "cbd-thc",
  "interacoes",
  "evidencias",
  "acompanhamento",
  "regulamentacao",
  "efeitos-colaterais",
  "perfil-paciente",
];

const caseStarterIds = ["caso1-inicio", "caso2-inicio"];

const qaMap: Record<string, QAPair> = Object.fromEntries(
  qaPairs.map((qa) => [qa.id, qa]),
);

interface SimMessage {
  id: number;
  text: string;
  from: "user" | "bot";
  qaId?: string;
}

const TYPING_DELAY_MS = 1200;

function TypingIndicator() {
  return (
    <div className="flex justify-center">
      <div className="backdrop-blur-xl rounded-2xl px-6 py-4 md:px-8 md:py-5 shadow-xl bg-gradient-to-r from-[#FF8904]/20 to-petrolio/20 border border-[#FF8904]/30">
        <div className="flex flex-col items-center space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-2.5 h-2.5 rounded-full animate-pulse shadow-lg bg-[#FF8904]"></div>
            <div className="w-2.5 h-2.5 rounded-full animate-pulse delay-75 shadow-lg bg-[#FF8904]"></div>
            <div className="w-2.5 h-2.5 rounded-full animate-pulse delay-150 shadow-lg bg-[#FF8904]"></div>
          </div>
          <Typography variant="body2" className="text-white font-semibold">
            Dr. Weedy está digitando...
          </Typography>
        </div>
      </div>
    </div>
  );
}

export default function QASimulator() {
  const [messages, setMessages] = useState<SimMessage[]>([]);
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const nextMsgId = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    // scrollTop direto no container, não scrollIntoView — scrollIntoView sobe
    // pelos ancestrais e acaba rolando a página inteira, não só a conversa.
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  function handleAsk(id: string) {
    const qa = qaMap[id];
    if (!qa || pendingId || answeredIds.includes(id)) return;

    // Leva o painel de conversa pra tela — no mobile os chips ficam no topo e
    // a resposta aparece bem mais abaixo; sem isso parece que nada aconteceu.
    // Alvo fixo (painel, não a última mensagem) e disparado só no clique, não
    // a cada mensagem nova — não é o mesmo scrollIntoView que já causou o bug
    // de rolar a página inteira quando ficava preso ao crescimento da lista.
    panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });

    setMessages((prev) => [
      ...prev,
      { id: nextMsgId.current++, text: qa.question, from: "user" },
    ]);
    setPendingId(id);

    timerRef.current = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: nextMsgId.current++, text: qa.answer, from: "bot", qaId: qa.id },
      ]);
      setAnsweredIds((prev) => [...prev, id]);
      setPendingId(null);
    }, TYPING_DELAY_MS);
  }

  function handleReset() {
    clearTimeout(timerRef.current);
    setMessages([]);
    setAnsweredIds([]);
    setPendingId(null);
  }

  function renderChip(id: string, variant: "default" | "case" = "default") {
    const qa = qaMap[id];
    if (!qa) return null;
    const answered = answeredIds.includes(id);
    const icon = answered ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined;
    return (
      <Chip
        key={id}
        label={qa.question}
        icon={icon}
        onClick={() => handleAsk(id)}
        disabled={Boolean(pendingId) || answered}
        sx={{
          color: "#ffffff",
          fontSize: { xs: "12px", md: "13px" },
          fontWeight: 500,
          padding: "4px 6px",
          height: "auto",
          "& .MuiChip-label": { whiteSpace: "normal", padding: "6px 4px" },
          backgroundColor: answered
            ? "rgba(255, 137, 4, 0.12)"
            : variant === "case"
              ? "rgba(255, 137, 4, 0.08)"
              : "rgba(255,255,255,0.08)",
          border:
            variant === "case" && !answered
              ? "1px solid rgba(255, 137, 4, 0.3)"
              : "1px solid rgba(255,255,255,0.15)",
          "&:hover": {
            backgroundColor: "rgba(255, 137, 4, 0.18)",
          },
          "&.Mui-disabled": {
            opacity: answered ? 0.75 : 0.4,
          },
        }}
      />
    );
  }

  const hasConversation = messages.length > 0;

  return (
    <section
      id="demonstracao"
      className="relative z-10 px-6 md:px-12 pb-20 md:pb-28"
    >
      <div className="text-center mb-10">
        <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/70 text-xs md:text-sm mb-4">
          Demonstração
        </div>
        <Typography
          variant="h4"
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontFamily: "Oxanium, sans-serif",
            letterSpacing: "-0.02em",
            marginBottom: "12px",
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Veja o Dr. Weedy em ação
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "1.05rem",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          Clique em uma pergunta rápida ou siga um caso clínico completo — o
          agente acompanha o contexto e traz novas perguntas relacionadas a
          cada resposta.
        </Typography>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <Typography
            sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginBottom: "10px" }}
          >
            Casos clínicos
          </Typography>
          <div
            className="flex flex-wrap justify-center gap-2 md:gap-3"
            role="group"
            aria-label="Casos clínicos"
          >
            {caseStarterIds.map((id) => renderChip(id, "case"))}
          </div>
        </div>

        <div className="mb-6">
          <Typography
            sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginBottom: "10px" }}
          >
            Perguntas rápidas
          </Typography>
          <div
            className="flex flex-wrap justify-center gap-2 md:gap-3"
            role="group"
            aria-label="Perguntas de exemplo"
          >
            {starterIds.map((id) => renderChip(id))}
          </div>
        </div>

        <Box
          ref={panelRef}
          sx={{
            background:
              "linear-gradient(135deg, rgba(26, 49, 60, 0.6) 0%, rgba(4, 9, 10, 0.8) 100%)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "24px",
            padding: { xs: "16px", md: "28px" },
          }}
        >
          <div
            ref={scrollContainerRef}
            aria-live="polite"
            className="max-h-[380px] md:max-h-[480px] overflow-y-auto flex flex-col gap-4 md:gap-6"
          >
            {!hasConversation && (
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "0.95rem",
                  textAlign: "center",
                  padding: "24px 0",
                }}
              >
                Escolha uma pergunta acima para começar.
              </Typography>
            )}
            {messages.map((m) => {
              const followUps = m.qaId ? qaMap[m.qaId]?.followUps : undefined;
              return (
                <div key={m.id}>
                  <ChatMessage message={{ id: m.id, text: m.text, from: m.from }} />
                  {followUps && followUps.length > 0 && (
                    <div className="ml-12 md:ml-[72px] mt-3 flex flex-col gap-2">
                      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>
                        Perguntas relacionadas:
                      </Typography>
                      <div className="flex flex-wrap gap-2">
                        {followUps.map((id) => renderChip(id))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {pendingId && <TypingIndicator />}
          </div>
        </Box>

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.5)", fontSize: { xs: "11px", md: "12px" } }}
          >
            Respostas pré-escritas para fins de demonstração, não geradas em tempo real
            por IA — apenas ilustrativas, sem valor de aconselhamento médico. Valores
            clínicos exatos aparecem propositalmente ocultos (`[██]`) nesta demo.
          </Typography>
          {hasConversation && (
            <Button
              onClick={handleReset}
              startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: "rgba(255,255,255,0.6)",
                textTransform: "none",
                fontSize: "13px",
                flexShrink: 0,
                "&:hover": { color: "#FF8904" },
              }}
            >
              Reiniciar conversa
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
